import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';

/**
 * The workspace's normal email-content retention window, in days.
 *
 * Stated here once so the disconnect dialog and the success message cannot drift apart —
 * and deliberately NOT added to the 30-day disconnect grace anywhere in the copy. They are
 * two independent limits and the earlier one always wins; implying 120 days would be the
 * easiest possible way to make this feature lie.
 */
const RETENTION_DAYS = 90;
import {
  useConnectionsQuery, useConnectKeapMutation, useVerifyKeapMutation,
  useConnectAnthropicMutation, useRemoveAnthropicKeyMutation, useStartGmailMutation, useDisconnectGmailMutation,
  useSyncGmailMutation, useStartMicrosoftMutation, useDisconnectMicrosoftMutation,
  useSyncMicrosoftMutation, useDeleteHistoricalMailboxMutation,
  type Connector, type ConnectorCapability, type SyncReport,
} from './api.js';
import {
  Alert, Button, Card, EmptyState, Spinner, StatusBadge, STATUS_EXPLANATION, formatWhen,
} from './ui.js';
import { rememberUpgrade } from './oauthOutcome.js';

/** Where every consent flow started from this screen returns. */
const RETURN_PATH = '/settings/connections';

/**
 * Supplies the Service Account Key.
 *
 * The value lives in component state only while the form is open, and is cleared the
 * moment the request resolves — success or failure. It is never placed in a URL, never
 * logged, and no response ever returns it.
 */
function KeapKeyForm({ onDone, hasExistingKey }: { onDone: () => void; hasExistingKey: boolean }) {
  const [key, setKey] = useState('');
  const [connect, { isLoading, error }] = useConnectKeapMutation();
  const [failed, setFailed] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFailed(null);
    try {
      await connect({ serviceAccountKey: key }).unwrap();
      onDone();
    } catch {
      // The server's message is intentionally generic; it never echoes the value.
      setFailed('The key could not be stored. Check that you pasted it completely.');
    } finally {
      setKey('');
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 space-y-3 border-t border-slate-200 pt-4">
      <label className="block text-sm">
        <span className="font-medium text-slate-700">
          {hasExistingKey ? 'Replace the Service Account Key' : 'Service Account Key'}
        </span>
        <input
          type="password"
          autoComplete="off"
          spellCheck={false}
          required
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Paste the key from your Keap account"
          className="mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
        />
      </label>
      <p className="text-xs text-slate-500">
        The key is encrypted before it is stored and is never shown again. Storing it
        contacts Keap in no way — verify it afterwards to confirm it works.
      </p>
      {failed || error ? <Alert tone="error">{failed ?? 'The key could not be stored.'}</Alert> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="min-h-11" disabled={isLoading || key.length === 0}>
          {isLoading ? 'Storing…' : 'Store key'}
        </Button>
        <Button variant="secondary" className="min-h-11" onClick={() => { setKey(''); onDone(); }}>Cancel</Button>
      </div>
    </form>
  );
}

/**
 * The Anthropic API key.
 *
 * Same handling as the CRM key: a password field, cleared as soon as the request
 * resolves, sent over the same-origin session, and never returned by any response.
 */
function AiKeyForm({ onDone, hasExistingKey }: { onDone: () => void; hasExistingKey: boolean }) {
  const [key, setKey] = useState('');
  const [anthropicWorkspaceId, setAnthropicWorkspaceId] = useState('');
  const [connect, { isLoading }] = useConnectAnthropicMutation();
  const [failed, setFailed] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setFailed(null);
    try {
      await connect({
        apiKey: key,
        anthropicWorkspaceId: anthropicWorkspaceId.trim() || null,
      }).unwrap();
      onDone();
    } catch {
      setFailed('The key could not be stored. Check that you pasted it completely.');
    } finally {
      setKey('');
    }
  };

  return (
    <form onSubmit={submit} className="mt-4 space-y-3 border-t border-slate-200 pt-4">
      <label className="block text-sm">
        <span className="font-medium text-slate-700">
          {hasExistingKey ? 'Replace the API key' : 'Anthropic API key'}
        </span>
        <input
          type="password" autoComplete="off" spellCheck={false} required
          value={key} onChange={(e) => setKey(e.target.value)}
          placeholder="Paste the key from your Anthropic account"
          className="mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
        />
      </label>
      <label className="block text-sm">
        <span className="font-medium text-slate-700">Anthropic workspace ID (optional)</span>
        <input
          type="text" autoComplete="off" spellCheck={false}
          value={anthropicWorkspaceId}
          onChange={(e) => setAnthropicWorkspaceId(e.target.value)}
          placeholder="wrkspc_…"
          className="mt-1 min-h-11 w-full min-w-0 rounded-md border border-slate-300 px-3 py-2 font-mono text-sm"
        />
        <span className="mt-1 block text-xs text-slate-500">
          Only needed if your key belongs to you rather than to one Anthropic workspace.
          If reading a card fails asking for a workspace ID, copy it from the Anthropic
          console and add it here. It is not a secret.
        </span>
      </label>
      <p className="text-xs text-slate-500">
        The key is encrypted before it is stored and never shown again. Storing it
        contacts Anthropic in no way — the first card you read is what proves it works.
      </p>
      {failed ? <Alert tone="error">{failed}</Alert> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" className="min-h-11" disabled={isLoading || key.length === 0}>
          {isLoading ? 'Storing…' : 'Store key'}
        </Button>
        <Button variant="secondary" className="min-h-11" onClick={() => { setKey(''); onDone(); }}>Cancel</Button>
      </div>
    </form>
  );
}

/* ── One card system for every connection ─────────────────────────────── */

const FOCUS = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900';
/** Every action in a card footer is the same height, whatever kind of control it is. */
const ACTION = 'min-h-11';
const SECONDARY_ACTION = `inline-flex min-h-11 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 ${FOCUS}`;

/**
 * The shape every connection takes: a header (what it is, and its status), one sentence,
 * the facts, then a footer of actions — primary first — pinned to the bottom so cards in
 * one row line their buttons up. Forms and confirmations open beneath the footer, next to
 * the button that opened them.
 *
 * On a phone the actions stack at full width, one per row. Left to wrap, three buttons of
 * different widths broke into one-and-two (or two-and-one) depending on the label, so the
 * same card looked different at 320, 375 and 430.
 */
const ACTION_ROW = 'flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center';
function ConnectionCard({ title, subtitle, status, description, children, actions, after, className = '' }: {
  title: string;
  subtitle?: ReactNode;
  status: Connector['status'];
  description: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  after?: ReactNode;
  className?: string;
}) {
  return (
    <Card className={`flex min-w-0 flex-col p-5 ${className}`}>
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {subtitle ? <div className="mt-0.5 text-sm font-medium text-slate-700">{subtitle}</div> : null}
        </div>
        <div className="shrink-0"><StatusBadge status={status} /></div>
      </div>
      <div className="mt-2 text-sm text-slate-600">{description}</div>
      {children}
      {actions ? (
        <div className="mt-auto pt-4">
          <div className={`${ACTION_ROW} border-t border-slate-100 pt-4`} data-connection-actions="">
            {actions}
          </div>
        </div>
      ) : null}
      {after}
    </Card>
  );
}

/** The facts about a connection, label above value on a phone and beside it from `sm`. */
function Meta({ rows }: { rows: ReadonlyArray<readonly [string, ReactNode] | null | false> }) {
  const shown = rows.filter((r): r is readonly [string, ReactNode] => Boolean(r));
  if (shown.length === 0) return null;
  return (
    <dl className="mt-4 min-w-0 space-y-2 text-sm">
      {shown.map(([label, value]) => (
        <div key={label} className="grid min-w-0 gap-x-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
          <dt className="text-xs text-slate-500 sm:text-sm">{label}</dt>
          <dd className="min-w-0 text-slate-800 [overflow-wrap:anywhere]">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function AiConnectorCard({ connector }: { connector: Connector }) {
  const [showForm, setShowForm] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [removeKey, { isLoading: removing }] = useRemoveAnthropicKeyMutation();
  // The customer's key, so the customer can take it back.
  const hasKey = connector.status !== 'DISCONNECTED' && connector.status !== 'NOT_CONNECTED'
    && Boolean(connector.connectionId);
  return (
    <ConnectionCard
      title="AI"
      subtitle="Anthropic"
      status={connector.status}
      className="h-full"
      description={
        /*
         * Provider-neutral, because the key is no longer proved by one feature: it reads
         * marked emails and business cards, and verification runs on invented content
         * rather than on a customer's card or a real message.
         */
        `${connector.status === 'CONNECTED'
          ? 'Reads the emails you mark and the business cards you photograph. Working.'
          : connector.status === 'REAUTH_REQUIRED'
            ? 'The key was rejected. Supply a new one.'
            : connector.status === 'PENDING_VERIFICATION'
              ? 'A key is stored. It has not been used yet, so it is unproven.'
              : 'No key stored yet. Reading marked emails and business cards both need one.'
        } It is your own Anthropic key, so Anthropic bills your account for what it reads.`
      }
      actions={(
        <>
          <Button
            variant={hasKey ? 'secondary' : 'primary'} className={ACTION}
            onClick={() => setShowForm((v) => !v)}
          >
            {hasKey ? 'Replace key' : 'Add key'}
          </Button>
          {hasKey ? (
            <Button variant="secondary" className={ACTION} onClick={() => setConfirmRemove(true)}>
              Remove key
            </Button>
          ) : null}
        </>
      )}
      after={showForm ? (
        <AiKeyForm hasExistingKey={hasKey} onDone={() => setShowForm(false)} />
      ) : confirmRemove ? (
        <div className="mt-3 space-y-2 rounded-md border border-slate-200 p-3" data-ai-remove-confirm="">
          <p className="text-sm text-slate-700">
            Remove the key? Marked emails and business cards will not be read until you add one again.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="primary" className={ACTION} disabled={removing}
              onClick={() => { void removeKey().then(() => setConfirmRemove(false)); }}
            >
              Remove key
            </Button>
            <Button variant="secondary" className={ACTION} onClick={() => setConfirmRemove(false)}>
              Keep it
            </Button>
          </div>
        </div>
      ) : null}
    >
      <Meta rows={[connector.connectionId ? ['Last verified', formatWhen(connector.lastVerifiedAt)] : null]} />
    </ConnectionCard>
  );
}

/**
 * The parts of a mailbox connector that genuinely differ by provider.
 *
 * Everything else — the layout, the counts, the confirmation, the read controls — is one
 * component used twice. Two copies of this card would drift, and the way it would drift
 * is that one provider quietly stops saying "read-only" somewhere.
 */
interface MailboxProviderCopy {
  readonly signInAt: string;
  /** The product's own name, as the card shows it. */
  readonly providerName: string;
  readonly mailboxWord: string;
  readonly connectLabel: string;
  readonly rateLimitCode: string;
  /** The button that adds the optional capability to this same connection. */
  readonly allowLabel: string;
}

const MAILBOX_COPY: Record<string, MailboxProviderCopy> = {
  gmail: {
    signInAt: 'Google',
    providerName: 'Gmail',
    mailboxWord: 'Gmail',
    connectLabel: 'Connect Gmail',
    rateLimitCode: 'GMAIL_RATE_LIMIT',
    allowLabel: 'Allow label creation',
  },
  microsoft: {
    signInAt: 'Microsoft',
    providerName: 'Microsoft Outlook',
    mailboxWord: 'Outlook',
    connectLabel: 'Connect Microsoft Outlook',
    rateLimitCode: 'MS_THROTTLED',
    allowLabel: 'Allow category creation',
  },
};

/**
 * How a read is described afterwards.
 *
 * Counts, in a sentence, because "we listed 10, 7 were already here, 3 were new" is
 * what tells someone the boundary held — and because nothing about what the messages
 * said belongs on this screen.
 */
function describeSync(r: SyncReport, mailboxWord: string): string {
  // Under email capture a sync reads only MARKED messages; with no marker it reads none.
  if (r.captureConfigured === false) {
    return `Checked ${mailboxWord}. No capture label or category is chosen yet, so nothing was read.`;
  }
  const parts = [`Read ${r.listed} marked message${r.listed === 1 ? '' : 's'} from ${mailboxWord}.`];
  if (r.alreadyHeld > 0) parts.push(`${r.alreadyHeld} already held, so ${r.alreadyHeld === 1 ? 'it was' : 'they were'} not downloaded again.`);
  /*
   * What is TRUE of the messages, not what was written to a queue.
   *
   * This used to say "N queued for analysis", counting outbox rows. An outbox row is
   * durable INTENT, not work in progress — and with mailbox analysis switched off no
   * analysis happens at all, so the sentence promised something that could not occur.
   * The messages are stored; whether they are ever analysed is a decision the operator
   * has not yet taken.
   */
  parts.push(`${r.inserted} new.`);
  parts.push(
    r.inserted === 1
      ? 'It was sent to Review as a CRM capture. Unmarked mail was not read.'
      : 'They were sent to Review as CRM captures. Unmarked mail was not read.',
  );
  if (r.attachmentsRecorded > 0) parts.push(`${r.attachmentsRecorded} attachment${r.attachmentsRecorded === 1 ? '' : 's'} noted — no file contents were downloaded.`);
  if (r.skipped > 0) parts.push(`${r.skipped} could not be read and ${r.skipped === 1 ? 'was' : 'were'} skipped.`);
  // Distinct from "could not be read": these were deleted between being listed and
  // being fetched, which is not a fault and should not read like one.
  if (r.vanished && r.vanished > 0) parts.push(`${r.vanished} ${r.vanished === 1 ? 'was' : 'were'} deleted from the mailbox before ${r.vanished === 1 ? 'it' : 'they'} could be read.`);
  // Only Outlook reports removals, and only when there were some. Gmail's feed is never
  // asked about them, so its sentence stays silent rather than claiming zero.
  if (r.removed && r.removed > 0) parts.push(`${r.removed} had been deleted from the mailbox and ${r.removed === 1 ? 'was' : 'were'} not re-read.`);
  if (r.recoveredFromExpiredCursor) parts.push('The resume point had expired, so a recent window was re-checked instead.');
  return parts.join(' ');
}

/**
 * One line about whether mail is currently flowing.
 *
 * No provider error codes, no queue vocabulary, no exception text — a category and a time.
 * Someone reading this needs to know whether to act, and for every state but `blocked` the
 * answer is no.
 */
function SyncLine({ sync }: { sync: NonNullable<Connector['sync']> }) {
  if (sync.state === 'healthy' || sync.state === 'idle') return null;

  const text = sync.state === 'syncing'
    ? 'Reading new mail now.'
    : sync.state === 'blocked'
      ? 'Paused until this mailbox is reconnected.'
      : sync.lastErrorCategory === 'rate_limit'
        ? 'Sync delayed — the provider is limiting how fast we can read. Retrying automatically.'
        : 'Sync delayed — retrying automatically.';

  return (
    <p className="mt-1 text-sm text-amber-800">
      {text}
      {sync.lastSuccessfulSyncAt ? (
        <span className="block text-xs text-slate-500">
          Last received mail {formatWhen(sync.lastSuccessfulSyncAt)}
          {sync.retryAt ? ` \u00b7 next attempt ${formatWhen(sync.retryAt)}` : ''}
        </span>
      ) : null}
    </p>
  );
}


/**
 * What the grant lets ContactPilot do, as a checklist under the connected account.
 *
 * An optional capability is an UPGRADE of this same connection — the same account, the
 * same stored mail — so its control says "Allow …", never "Reconnect". Reconnecting is
 * what somebody does when access broke; asking for one more permission is not that, and
 * calling it that made people think they were about to lose their mailbox.
 */
function CapabilityChecklist({ connector, copy }: {
  connector: Connector; copy: MailboxProviderCopy;
}) {
  const [startGmail, gmailState] = useStartGmailMutation();
  const [startMs, msState] = useStartMicrosoftMutation();
  const [error, setError] = useState<string | null>(null);
  const caps = connector.capabilities ?? [];
  if (caps.length === 0) return null;
  const busy = gmailState.isLoading || msState.isLoading;

  const allow = async (cap: ConnectorCapability) => {
    setError(null);
    try {
      const { authorizationUrl } = cap.upgrade === 'categoryManagement'
        ? await startMs({ categoryManagement: true, returnPath: RETURN_PATH }).unwrap()
        : await startGmail({ labelManagement: true, returnPath: RETURN_PATH }).unwrap();
      rememberUpgrade(connector.kind === 'microsoft' ? 'microsoft' : 'gmail');
      window.location.assign(authorizationUrl);
    } catch {
      setError(`${copy.signInAt} could not be opened. Try again.`);
    }
  };

  return (
    <div className="mt-4">
      <p className="text-sm font-medium text-slate-700">What ContactPilot can do</p>
      <ul className="mt-2 space-y-3" aria-label="What this connection allows" data-capabilities="">
        {caps.map((c) => (
          <li key={c.key} className="flex min-w-0 items-start gap-3 text-sm">
            <span
              aria-hidden="true"
              className={`mt-0.5 w-4 shrink-0 text-center ${c.granted ? 'text-emerald-700' : 'text-slate-400'}`}
            >
              {c.granted ? '✓' : '○'}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-slate-800 [overflow-wrap:anywhere]">
                {c.label}
                <span className="sr-only">{c.granted ? ' — allowed' : ' — not allowed yet'}</span>
              </p>
              {c.detail ? (
                <p className="mt-0.5 text-xs text-slate-500 [overflow-wrap:anywhere]">{c.detail}</p>
              ) : null}
              {!c.granted && c.upgrade ? (
                <Button
                  variant="secondary" className="mt-2 min-h-11"
                  disabled={busy}
                  onClick={() => void allow(c)}
                >
                  {busy ? `Opening ${copy.signInAt}…` : c.upgrade === 'categoryManagement' ? 'Allow category creation' : copy.allowLabel}
                </Button>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
      {error ? <div className="mt-2"><Alert tone="error">{error}</Alert></div> : null}
    </div>
  );
}

/**
 * Stored mail from a mailbox that is not a connection any more.
 *
 * Deliberately outside the list that describes the live mailbox and deliberately not
 * styled as an error. Nothing is broken; there is simply data here, and the only two
 * honest things to say about it are how much there is and when — or whether — it goes.
 */
function HistoricalMailboxes({ historical, onOutcome }: {
  historical: NonNullable<Connector['historical']>;
  onOutcome: (o: { tone: 'success' | 'error'; text: string } | null) => void;
}) {
  const [deleteHistorical, { isLoading: deletingHistorical }] = useDeleteHistoricalMailboxMutation();
  /** Which historical mailbox is being confirmed for deletion, if any. */
  const [confirmingDelete, setConfirmingDelete] = useState<string | null>(null);

  /*
   * Deletes a historical mailbox, and says what was actually removed.
   *
   * The count comes back from the server for the same reason the disconnect banner does:
   * this page has already once told somebody their mail was gone when it was not.
   */
  const runHistoricalDelete = async (emailAccountId: string) => {
    onOutcome(null);
    try {
      const res = await deleteHistorical({ emailAccountId }).unwrap();
      setConfirmingDelete(null);
      onOutcome({
        tone: 'success',
        text: res.deleted
          ? `Deleted ${res.messagesRemoved} stored message${res.messagesRemoved === 1 ? '' : 's'} `
            + `and everything derived from them.`
          : 'That mailbox had already been deleted.',
      });
    } catch {
      onOutcome({ tone: 'error', text: 'The stored data could not be deleted. Nothing was removed.' });
    }
  };

  if (historical.length === 0) return null;
  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4">
      <h4 className="text-sm font-medium text-slate-900">Stored mail from an earlier mailbox</h4>
      <p className="mt-1 text-sm text-slate-600">
        Not connected and not syncing. Nothing is being read from it.
      </p>
      {historical.map((h) => (
        <div key={h.emailAccountId} className="mt-3 border-t border-slate-200 pt-3">
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <div className="flex min-w-0 justify-between gap-4 sm:block">
              <dt className="text-slate-500">Mailbox</dt>
              <dd className="min-w-0 text-slate-800 [overflow-wrap:anywhere]">{h.addressNormalized}</dd>
            </div>
            <div className="flex justify-between gap-4 sm:block">
              <dt className="text-slate-500">Messages stored</dt>
              <dd className="text-slate-800">
                {h.messages} in {h.threads} thread{h.threads === 1 ? '' : 's'}
              </dd>
            </div>
            <div className="flex justify-between gap-4 sm:block">
              <dt className="text-slate-500">Deletion</dt>
              <dd className="text-right text-slate-800 sm:text-left">
                {/*
                  Null is shown as null. A mailbox that lost the slot without being
                  explicitly disconnected has no clock running, and printing a confident
                  date would promise a deletion nothing is scheduled to do.
                */}
                {h.retainedUntil
                  ? `Scheduled for ${formatWhen(h.retainedUntil)}`
                  : 'Not scheduled — delete it here when you are ready'}
              </dd>
            </div>
          </dl>
          {confirmingDelete === h.emailAccountId ? (
            <div className="mt-3 rounded-md border border-red-200 bg-white p-3">
              <p className="text-sm text-slate-700">
                Permanently delete {h.messages} stored message
                {h.messages === 1 ? '' : 's'} and everything ContactPilot derived from
                them, including review decisions. This cannot be undone.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  className="min-h-11"
                  onClick={() => void runHistoricalDelete(h.emailAccountId)}
                  disabled={deletingHistorical}
                >
                  {deletingHistorical ? 'Deleting…' : 'Delete permanently'}
                </Button>
                <Button variant="secondary" className="min-h-11" onClick={() => setConfirmingDelete(null)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="mt-3">
              <Button variant="secondary" className="min-h-11" onClick={() => setConfirmingDelete(h.emailAccountId)}>
                Delete stored data
              </Button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * "Change email provider": the one-mailbox rule, said before anything is undone.
 *
 * A dialog, and a real one — named by its heading, focus moved in and kept in, Escape and
 * Cancel close it and focus returns to the button that opened it — because it interrupts
 * on purpose. It changes nothing itself: its one action opens the ordinary disconnect
 * choice (keep for 30 days, or delete now), so switching provider can never be a quieter
 * way to lose stored mail.
 */
function ChangeProviderDialog({ current, other, onDisconnect, onCancel }: {
  current: string; other: string; onDisconnect: () => void; onCancel: () => void;
}) {
  const headingId = useId();
  const bodyId = useId();
  const firstRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); return; }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      // Two buttons; Tab cycles between them rather than escaping behind the overlay.
      const buttons = Array.from(dialogRef.current.querySelectorAll<HTMLButtonElement>('button'));
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/30 p-0 sm:items-center sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={headingId}
        aria-describedby={bodyId}
        data-change-provider=""
        className="w-full max-w-md rounded-t-lg bg-white p-5 shadow-xl sm:rounded-lg"
      >
        <h3 id={headingId} className="text-base font-semibold text-slate-900">Change email provider</h3>
        <p id={bodyId} className="mt-2 text-sm text-slate-700">
          ContactPilot supports one active mailbox per workspace. Disconnect {current} to
          connect {other} instead.
        </p>
        <div className={`mt-4 ${ACTION_ROW}`}>
          <button
            ref={firstRef}
            type="button"
            onClick={onDisconnect}
            className="inline-flex min-h-11 items-center justify-center rounded-md bg-slate-900 px-3 text-sm font-medium text-white hover:bg-slate-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            Disconnect {current}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex min-h-11 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * The workspace's one mailbox: a consent flow rather than a key.
 *
 * There is no field to paste anything into, which is the point — the credential is
 * issued by the provider to us and never passes through the browser. What the card
 * shows is which mailbox is connected, what it allows, and how to stop.
 */
function MailboxConnectorCard({ connector, historical }: {
  connector: Connector;
  /** Stored mail from any earlier mailbox, whichever provider it came from. */
  historical: NonNullable<Connector['historical']>;
}) {
  /*
   * All six hooks run on every render, for either provider, and the pair that matters is
   * chosen afterwards. Calling them conditionally would break the rules of hooks; this
   * costs nothing, because an unused RTK Query mutation trigger issues no request.
   */
  const [startGmail, { isLoading: startingGmail }] = useStartGmailMutation();
  const [disconnectGmail, { isLoading: disconnectingGmail }] = useDisconnectGmailMutation();
  const [syncGmail, { isLoading: syncingGmail }] = useSyncGmailMutation();
  const [startMs, { isLoading: startingMs }] = useStartMicrosoftMutation();
  const [disconnectMs, { isLoading: disconnectingMs }] = useDisconnectMicrosoftMutation();
  const [syncMs, { isLoading: syncingMs }] = useSyncMicrosoftMutation();

  const isMicrosoft = connector.kind === 'microsoft';
  const copy = MAILBOX_COPY[connector.kind] ?? MAILBOX_COPY.gmail!;
  const sync = isMicrosoft ? syncMs : syncGmail;
  const starting = isMicrosoft ? startingMs : startingGmail;
  const disconnecting = isMicrosoft ? disconnectingMs : disconnectingGmail;
  const syncing = isMicrosoft ? syncingMs : syncingGmail;

  const [outcome, setOutcome] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);
  // Keeping is the default: routine connection management must not destroy history.
  const [deleteData, setDeleteData] = useState(false);
  const [confirmedDelete, setConfirmedDelete] = useState(false);
  const [changingProvider, setChangingProvider] = useState(false);
  const changeRef = useRef<HTMLButtonElement | null>(null);
  const confirmRef = useRef<HTMLParagraphElement | null>(null);
  // The disconnect choice takes focus when it opens, so the dialog's action lands somewhere.
  useEffect(() => { if (confirmingDisconnect) confirmRef.current?.focus(); }, [confirmingDisconnect]);

  /*
   * Reconnecting after access broke asks again for what the connection already had, so a
   * mailbox that was allowed to create labels does not silently lose that by recovering.
   */
  const hadCreate = (connector.capabilities ?? []).some((c) => c.key === 'create_markers' && c.granted);
  const reconnect = async () => {
    setOutcome(null);
    try {
      const { authorizationUrl } = isMicrosoft
        ? await startMs({ returnPath: RETURN_PATH, ...(hadCreate ? { categoryManagement: true } : {}) }).unwrap()
        : await startGmail({ returnPath: RETURN_PATH, ...(hadCreate ? { labelManagement: true } : {}) }).unwrap();
      // A full navigation, not a fetch: consent happens on the provider's own page.
      window.location.assign(authorizationUrl);
    } catch {
      setOutcome({ tone: 'error', text: 'The connection could not be started.' });
    }
  };

  /*
   * Says what actually happened to the data, which depends on what was chosen.
   */
  const runDisconnect = async (destructive: boolean) => {
    setOutcome(null);
    setConfirmingDisconnect(false);
    /*
     * What the SERVER did, not what was asked for.
     *
     * This once read `destructive` — the request — and so announced "stored messages and
     * the intelligence built from them have been deleted" whenever the box had been
     * ticked, whether or not anything was deleted. `deletedNow` is the server's own
     * answer and is the only thing entitled to produce that sentence.
     */
    const dataLineFor = (deletedNow: boolean) =>
      deletedNow
        ? ' Stored messages and the intelligence built from them have been deleted.'
        : destructive
          ? ' No stored data was deleted — there was none held by this connection.'
          : ` Stored data is kept for 30 days, and your normal ${RETENTION_DAYS}-day content limit still applies.`;
    try {
      if (isMicrosoft) {
        const res = await disconnectMs({ deleteData: destructive }).unwrap();
        const dataLine = dataLineFor(res.deletedNow === true);
        // It does not claim the grant is gone at Microsoft, because nothing this product
        // can call would have made that true.
        setOutcome({
          tone: 'success',
          text: `Disconnected.${dataLine} To remove the application from your Microsoft `
            + `account as well, visit ${res.revokeAtProviderUrl}.`,
        });
      } else {
        const res = await disconnectGmail({ deleteData: destructive }).unwrap();
        const dataLine = dataLineFor(res.deletedNow === true);
        setOutcome({
          tone: 'success',
          text: res.revokedAtGoogle
            ? `Disconnected. Access was revoked at Google.${dataLine}`
            : `Disconnected.${dataLine} Google could not be reached to revoke access — you `
              + 'can remove it from your Google account permissions.',
        });
      }
    } catch {
      setOutcome({ tone: 'error', text: 'The mailbox could not be disconnected.' });
    }
  };

  /*
   * One button, not a sample-size field and two modes. New marked mail arrives on its
   * own; this is for somebody who has just marked an email and does not want to wait.
   */
  const runSync = async () => {
    setOutcome(null);
    const mode = connector.mailbox?.hasCursor ? 'incremental' : 'initial';
    try {
      const res = await sync({ mode }).unwrap();
      setOutcome({ tone: 'success', text: describeSync(res.report, copy.mailboxWord) });
    } catch (e) {
      const code = (e as { data?: { error?: string } }).data?.error;
      setOutcome({
        tone: 'error',
        text: code === 'REAUTH_REQUIRED'
          ? 'Access to the mailbox has expired. Reconnect to resume.'
          : code === copy.rateLimitCode
            // A throttle is temporary and is NOT a broken connection. Saying "reconnect"
            // here would send someone through consent to fix a wait.
            ? `${copy.signInAt} is rate limiting this mailbox. Try again shortly.`
            : 'The mailbox could not be read.',
      });
    }
  };

  const connected = connector.status === 'CONNECTED';
  const needsReconnect = connector.status === 'REAUTH_REQUIRED';
  const mailbox = connector.mailbox ?? null;
  const syncHealth = connector.sync ?? null;
  /*
   * Whether there is a live mailbox to act on, decided by the server.
   *
   * This used to be inferred here from the status string, and inferring it is what put a
   * Disconnect button under a mailbox that no longer held the workspace's slot. The
   * request succeeded, did nothing, and reported that it had deleted eighty messages.
   */
  const canDisconnect = connector.canDisconnect === true;
  const otherName = isMicrosoft ? MAILBOX_COPY.gmail!.providerName : MAILBOX_COPY.microsoft!.providerName;

  return (
    <ConnectionCard
      title="Email"
      subtitle={<span data-mailbox-provider="">{copy.providerName}</span>}
      status={connector.status}
      className="lg:col-span-2"
      description={(
        <>
          <p>
            {needsReconnect
              ? <><span className="block font-medium">{copy.mailboxWord} needs to be reconnected.</span>
                Your {copy.mailboxWord} connection expired. Reconnect to continue capturing {isMicrosoft ? 'categorized' : 'labeled'} emails.
                Your mailbox, past captures and Review stay as they are.</>
              : connected
                ? 'Only emails you mark with a capture label or category are read. ContactPilot never sends, deletes or changes anything in your mailbox.'
                : STATUS_EXPLANATION[connector.status]}
          </p>
          {/*
            Sync health, said separately from the connection. A temporarily throttled
            mailbox is CONNECTED and delayed, not disconnected.
          */}
          {syncHealth ? <SyncLine sync={syncHealth} /> : null}
        </>
      )}
      actions={(needsReconnect || connected || canDisconnect) ? (
        <>
          {needsReconnect ? (
            <Button className={ACTION} onClick={() => void reconnect()} disabled={starting}>
              {starting ? `Opening ${copy.signInAt}…` : 'Reconnect'}
            </Button>
          ) : null}
          {connected ? (
            <Button className={ACTION} onClick={() => void runSync()} disabled={syncing}>
              {syncing ? 'Checking…' : 'Check for marked mail'}
            </Button>
          ) : null}
          {canDisconnect ? (
            <button
              ref={changeRef}
              type="button"
              onClick={() => setChangingProvider(true)}
              disabled={disconnecting}
              className={SECONDARY_ACTION}
            >
              Change email provider
            </button>
          ) : null}
          {canDisconnect ? (
            <Button variant="secondary" className={ACTION} onClick={() => setConfirmingDisconnect(true)} disabled={disconnecting}>
              Disconnect
            </Button>
          ) : null}
        </>
      ) : null}
      after={(
        <>
          {changingProvider ? (
            <ChangeProviderDialog
              current={copy.providerName}
              other={otherName}
              onCancel={() => { setChangingProvider(false); requestAnimationFrame(() => changeRef.current?.focus()); }}
              onDisconnect={() => { setChangingProvider(false); setConfirmingDisconnect(true); }}
            />
          ) : null}

          {confirmingDisconnect ? (
            <div className="mt-4 space-y-3 border-t border-slate-200 pt-4" data-disconnect-choice="">
              <p ref={confirmRef} tabIndex={-1} className="text-sm font-medium text-slate-900 focus:outline-none">Disconnect this mailbox?</p>
              <p className="text-sm text-slate-700">
                ContactPilot will stop reading new messages immediately.
              </p>

              {/*
                The choice, and the default. Keeping is the default, and deleting is a
                deliberate second decision.
              */}
              <fieldset className="space-y-2">
                <legend className="text-sm text-slate-700">
                  What should happen to data already stored from this mailbox?
                </legend>
                <label className="flex gap-2 text-sm">
                  <input
                    type="radio" name={`disposal-${connector.kind}`} className="mt-1 h-5 w-5 shrink-0"
                    checked={!deleteData} onChange={() => setDeleteData(false)}
                  />
                  <span>
                    <span className="font-medium text-slate-900">Keep for 30 days</span>
                    <span className="block text-slate-600">
                      Stored messages stay available for 30 days so you can reconnect this
                      mailbox. Your normal {RETENTION_DAYS}-day email-content limit still
                      applies — disconnecting does not extend it.
                    </span>
                  </span>
                </label>
                <label className="flex gap-2 text-sm">
                  <input
                    type="radio" name={`disposal-${connector.kind}`} className="mt-1 h-5 w-5 shrink-0"
                    checked={deleteData} onChange={() => setDeleteData(true)}
                  />
                  <span>
                    <span className="font-medium text-slate-900">Delete stored data now</span>
                    <span className="block text-slate-600">
                      Permanently remove stored messages and the ContactPilot intelligence
                      built from them. This cannot be undone.
                    </span>
                  </span>
                </label>
              </fieldset>

              {/*
                A second, explicit step for the destructive option only. Keeping needs no
                friction; deleting should not be one misclick away.
              */}
              {deleteData ? (
                <label className="flex gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox" className="mt-1 h-5 w-5 shrink-0"
                    checked={confirmedDelete}
                    onChange={(e) => setConfirmedDelete(e.target.checked)}
                  />
                  <span>I understand this permanently deletes the stored data.</span>
                </label>
              ) : null}

              <div className={ACTION_ROW}>
                <Button
                  className={ACTION}
                  onClick={() => void runDisconnect(deleteData)}
                  disabled={disconnecting || (deleteData && !confirmedDelete)}
                >
                  {disconnecting ? 'Disconnecting…' : 'Disconnect'}
                </Button>
                <Button variant="secondary" className={ACTION} onClick={() => setConfirmingDisconnect(false)}>Cancel</Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    >
      {/*
        The facts on the left, what the grant allows on the right, from `lg` where the card
        spans the row; stacked below that.
      */}
      <div className="min-w-0 lg:grid lg:grid-cols-2 lg:gap-8">
        <Meta rows={[
          connector.providerAccountId ? ['Address', <span key="a" className="[overflow-wrap:anywhere]">{connector.providerAccountId}</span>] : null,
          mailbox ? ['Last checked', formatWhen(mailbox.lastSyncedAt)] : null,
          /*
            "Stored" is what this number counts. It said "held", which claimed a decision
            had been taken about every one of them; held is a real, different state here.
          */
          mailbox ? ['Messages stored', `${mailbox.messages} in ${mailbox.threads} thread${mailbox.threads === 1 ? '' : 's'}`] : null,
        ]} />
        <CapabilityChecklist connector={connector} copy={copy} />
      </div>

      <HistoricalMailboxes historical={historical} onOutcome={setOutcome} />

      {outcome ? <div className="mt-4"><Alert tone={outcome.tone}>{outcome.text}</Alert></div> : null}
      {connected ? (
        <p className="mt-3 text-xs text-slate-500">
          New marked mail is picked up automatically. Only mail from the last 30 days is
          considered, and attachment contents are never downloaded.
        </p>
      ) : null}
    </ConnectionCard>
  );
}

/**
 * No mailbox holds the workspace's slot: one card, offering each provider.
 *
 * One card rather than two, because the customer is making one decision — which mailbox —
 * and two side-by-side tiles read as two things to set up. Stored mail from an earlier
 * mailbox still shows here, because it is still the customer's to keep or delete.
 */
function MailboxChooserCard({ connectors }: { connectors: Connector[] }) {
  const [startGmail, { isLoading: startingGmail }] = useStartGmailMutation();
  const [startMs, { isLoading: startingMs }] = useStartMicrosoftMutation();
  const [outcome, setOutcome] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);
  const available = connectors.filter((c) => c.available);
  const historical = connectors.flatMap((c) => c.historical ?? []);

  const connect = async (kind: string) => {
    setOutcome(null);
    try {
      const { authorizationUrl } = kind === 'microsoft'
        ? await startMs({ returnPath: RETURN_PATH }).unwrap()
        : await startGmail({ returnPath: RETURN_PATH }).unwrap();
      window.location.assign(authorizationUrl);
    } catch {
      setOutcome({ tone: 'error', text: 'The connection could not be started.' });
    }
  };

  return (
    <ConnectionCard
      title="Email"
      subtitle={available.length > 0 ? 'Connect your mailbox' : undefined}
      status={historical.length > 0 ? 'HISTORICAL_RETAINED' : 'NOT_CONNECTED'}
      className="lg:col-span-2"
      description={available.length === 0
        ? 'Mailbox connections are not available in this workspace.'
        : 'Then mark the emails you want in your CRM. Access is read-only.'}
      actions={available.length > 0 ? available.map((c) => {
        const copy = MAILBOX_COPY[c.kind] ?? MAILBOX_COPY.gmail!;
        const busy = c.kind === 'microsoft' ? startingMs : startingGmail;
        return (
          <Button
            key={c.kind}
            variant={c.kind === 'gmail' ? 'primary' : 'secondary'}
            className={ACTION}
            onClick={() => void connect(c.kind)}
            disabled={startingGmail || startingMs || c.canConnect === false}
          >
            {busy ? `Opening ${copy.signInAt}…` : copy.connectLabel}
          </Button>
        );
      }) : null}
    >
      {available.length > 0 ? (
        <p className="mt-3 text-xs text-slate-500">
          You will be sent to Google or Microsoft to sign in. We ask only to read messages
          — we cannot send, delete, or change anything in your mailbox, and we never use
          your mail to train a model. One mailbox can be connected per workspace.
        </p>
      ) : null}

      <HistoricalMailboxes historical={historical} onOutcome={setOutcome} />

      {outcome ? <div className="mt-4"><Alert tone={outcome.tone}>{outcome.text}</Alert></div> : null}
    </ConnectionCard>
  );
}

/** The CRM connection. Verifying is a read; nothing in the CRM changes from this card. */
function KeapCard({ connector, onOpenCrmTags }: { connector: Connector; onOpenCrmTags: () => void }) {
  const [verify, { isLoading: verifying }] = useVerifyKeapMutation();
  const [showForm, setShowForm] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const runVerify = async () => {
    setResult(null);
    try {
      const res = await verify().unwrap();
      setResult(res.connector.status === 'CONNECTED'
        ? { tone: 'success', text: `Verified. Keap returned ${res.tagCount} tag${res.tagCount === 1 ? '' : 's'}. No contact was read or changed.` }
        : { tone: 'error', text: 'Keap rejected the key. Supply a new one to reconnect.' });
    } catch {
      setResult({ tone: 'error', text: 'The verification call failed.' });
    }
  };

  return (
    <ConnectionCard
      title="CRM"
      subtitle={connector.name}
      status={connector.status}
      className="h-full"
      description={connector.available ? STATUS_EXPLANATION[connector.status] : 'Not available in this workspace.'}
      actions={connector.available ? (
        <>
          <Button className={ACTION} onClick={runVerify} disabled={!connector.canVerify || verifying}>
            {verifying ? 'Verifying…' : 'Verify connection'}
          </Button>
          <Button variant="secondary" className={ACTION} onClick={() => setShowForm((v) => !v)}>
            {connector.connectionId ? 'Replace key' : 'Add key'}
          </Button>
          <button type="button" onClick={onOpenCrmTags} className={SECONDARY_ACTION}>
            Manage CRM tags
          </button>
        </>
      ) : null}
      after={showForm ? (
        <KeapKeyForm hasExistingKey={Boolean(connector.connectionId)} onDone={() => setShowForm(false)} />
      ) : null}
    >
      <Meta rows={[
        connector.available && connector.providerAccountId ? ['Account', connector.providerAccountId] : null,
        connector.available && connector.connectionId ? ['Last verified', formatWhen(connector.lastVerifiedAt)] : null,
      ]} />
      {result ? <div className="mt-4"><Alert tone={result.tone}>{result.text}</Alert></div> : null}
    </ConnectionCard>
  );
}

/** A mailbox connector that holds, or is recovering, the workspace's one slot. */
const holdsSlot = (c: Connector) =>
  c.canDisconnect === true || c.status === 'CONNECTED' || c.status === 'REAUTH_REQUIRED';

export function Connections({ onOpenCrmTags }: { onOpenCrmTags: () => void }) {
  const { data, isLoading, error } = useConnectionsQuery();

  if (isLoading) return <Spinner label="Loading connections…" />;
  if (error || !data) return <Alert tone="error">Connections could not be loaded.</Alert>;

  /*
   * One card per relationship the customer has with a provider: their CRM, their AI key,
   * and their one mailbox. Connectors that are not offered here are not shown at all,
   * rather than as tiles saying they are not available.
   */
  const keap = data.connectors.find((c) => c.kind === 'keap') ?? null;
  const ai = data.connectors.find((c) => c.kind === 'anthropic') ?? null;
  const mailboxes = data.connectors.filter((c) => c.kind === 'gmail' || c.kind === 'microsoft');
  const active = mailboxes.find(holdsSlot) ?? null;

  if (!keap && !ai && mailboxes.length === 0) {
    return <EmptyState title="Nothing to connect">No connections are available in this workspace.</EmptyState>;
  }

  return (
    /*
      The mailbox spans the row; the CRM and AI cards share the next one at equal height,
      so their headers, facts and footers line up without stretching a short card to the
      height of the tall one.
    */
    <div className="grid min-w-0 gap-4 lg:grid-cols-2">
      {mailboxes.length > 0 ? (
        active
          ? <MailboxConnectorCard connector={active} historical={mailboxes.flatMap((c) => c.historical ?? [])} />
          : <MailboxChooserCard connectors={mailboxes} />
      ) : null}
      {keap ? <KeapCard connector={keap} onOpenCrmTags={onOpenCrmTags} /> : null}
      {ai ? <AiConnectorCard connector={ai} /> : null}
    </div>
  );
}
